import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  Account_Invite,
  Account_Update,
  CompliantType,
  MatchKeyMatchableType,
  ModelError_Item,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { CompliancePanel } from '~components/CompliancePanel/CompliancePanel';
import MatchKeysSection from '~components/MatchKeys/MatchKeysSection';
import { Account } from '~hooks/useAccount/models';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ConnectedAccountContactPanel, {
  ConnectedAccountContactPanelHandler,
} from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import NotesPanel, { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import TruckCapacityPanel, {
  TruckCapacityPanelHandler,
} from '../TruckCapacityPanel/TruckCapacityPanel';
import AccountConfigurationPanel, {
  AccountConfigurationPanelHandler,
} from './AccountConfigurationPanel/AccountConfigurationPanel';
import AccountDetailsPanel, {
  AccountDetailsPanelHandler,
} from './AccountDetailsPanel/AccountDetailsPanel';
import {
  collectConfigurationForNewAccount,
  collectContactsForNewAccount,
  collectDetailsForNewAccount,
  collectNotesForNewAccount,
  createAccount,
} from './create-account-helpers';
import {
  collectConfigurationToUpdateAccount,
  collectContactsToUpdateAccount,
  collectDetailsToUpdateAccount,
  collectNotesToUpdateAccount,
  collectTruckCapacityToUpdateAccount,
  updateAccount,
} from './update-account-helpers';

export interface VendorAccountFormHandler {
  resetForm: () => void;
  submitForm: () => void;
}

interface VendorAccountFormProps {
  account?: Account;
  onAccountCreated?: (account: Account) => void;
  onAccountUpdated?: (account: Account) => void;
  onApiResponseErrorsChanged?: (hasErrors: boolean) => void;
  onComplianceEditingChange?: (isEditing: boolean) => void;
  onSavingStatusChanged?: (isSaving: boolean) => void;
  onSelectedCompanyChange?: (companyId: string) => void;
  onStagedComplianceDocsChange?: (docs: StagedComplianceDoc[]) => void;
  stackedLayout?: boolean;
}

const VendorAccountForm = forwardRef<VendorAccountFormHandler, VendorAccountFormProps>(
  function VendorAccountForm(
    {
      account,
      onAccountCreated,
      onAccountUpdated,
      onApiResponseErrorsChanged,
      onComplianceEditingChange,
      onSavingStatusChanged,
      onSelectedCompanyChange,
      onStagedComplianceDocsChange,
      stackedLayout,
    },
    ref,
  ) {
    const { userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);

    const accountDetailsPanelRef = useRef<AccountDetailsPanelHandler>(null);
    const connectedAccountContactPanelRef =
      useRef<ConnectedAccountContactPanelHandler>(null);
    const accountConfigurationPanelRef = useRef<AccountConfigurationPanelHandler>(null);
    const notesPanelRef = useRef<NotesPanelHandler>(null);
    const truckCapacityPanelRef = useRef<TruckCapacityPanelHandler>(null);

    const [isSavingAccount, setIsSavingAccount] = useState(false);
    const [apiResponseErrors, setApiResponseErrors] = useState<ModelError_Item[]>();
    const [selectedCompanyId, setSelectedCompanyId] = useState(companyId);

    const isEditing = Boolean(account?.id);

    const handleSubmit = useCallback(async () => {
      try {
        const accountDetails = isEditing
          ? await collectDetailsToUpdateAccount(accountDetailsPanelRef)
          : await collectDetailsForNewAccount(accountDetailsPanelRef);

        const accountContact = isEditing
          ? await collectContactsToUpdateAccount(connectedAccountContactPanelRef)
          : await collectContactsForNewAccount(connectedAccountContactPanelRef);

        const accountConfiguration = isEditing
          ? await collectConfigurationToUpdateAccount(accountConfigurationPanelRef)
          : await collectConfigurationForNewAccount(accountConfigurationPanelRef);

        const notes = isEditing
          ? await collectNotesToUpdateAccount(notesPanelRef)
          : await collectNotesForNewAccount(notesPanelRef);

        const truckCapacity = isEditing
          ? await collectTruckCapacityToUpdateAccount(truckCapacityPanelRef)
          : {};

        if (
          accountDetails?.values &&
          accountContact &&
          accountConfiguration &&
          notes &&
          truckCapacity
        ) {
          setApiResponseErrors(undefined);

          if (isEditing) {
            if (!account?.id) {
              return;
            }

            setIsSavingAccount(true);

            const values = {
              ...accountDetails.values,
              ...accountContact,
              ...accountConfiguration,
              ...notes,
              ...truckCapacity,
            } as Account_Update;

            const { account: updatedAccount, errors } = await updateAccount(
              values,
              account.id,
              accountDetails.companyId || companyId,
            );

            setApiResponseErrors(errors);
            setIsSavingAccount(false);

            if (updatedAccount) {
              onAccountUpdated?.(updatedAccount);
            }
          } else {
            setIsSavingAccount(true);

            const values = {
              ...accountDetails.values,
              ...accountContact,
              ...accountConfiguration,
              ...notes,
            } as Account_Invite;

            const { account, errors } = await createAccount(
              {
                name: values.name,
                contact: values.contact,
                address: values.address,
                account_types: values.account_types,
                department_id: values.department_id,
                notes: values.notes,
                external_id: values.external_id,
                auto_accept_orders: values.auto_accept_orders,
                send_invite: values.send_invite,
                company_type: values.company_type,
                label_ids: values.label_ids,
              },
              accountDetails.companyId || companyId,
            );

            setApiResponseErrors(errors);
            setIsSavingAccount(false);

            if (account) {
              onAccountCreated?.(account);
            }
          }
        }
      } catch (error) {
        setIsSavingAccount(false);

        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_create'),
        );
      }
    }, [account?.id, companyId, isEditing, onAccountCreated, onAccountUpdated]);

    useEffect(() => {
      onSavingStatusChanged?.(isSavingAccount);
    }, [onSavingStatusChanged, isSavingAccount]);

    useEffect(() => {
      onApiResponseErrorsChanged?.(Boolean(apiResponseErrors));
    }, [onApiResponseErrorsChanged, apiResponseErrors]);

    useEffect(() => {
      onSelectedCompanyChange?.(selectedCompanyId);
    }, [onSelectedCompanyChange, selectedCompanyId]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm() {
          handleSubmit();
        },
        resetForm() {
          setApiResponseErrors(undefined);
          setIsSavingAccount(false);
        },
      }),
      [handleSubmit],
    );

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: stackedLayout ? 12 : 6 }}>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <PanelWrapper stackedLayout={stackedLayout} sx={{ p: 2, mb: 2 }}>
                <PanelTitle
                  stackedLayout={stackedLayout}
                  sx={{ mb: stackedLayout ? 1 : 2 }}
                  title={t('account.form.account_details')}
                />

                <AccountDetailsPanel
                  ref={accountDetailsPanelRef}
                  account={account}
                  apiResponseErrors={apiResponseErrors}
                  onCompanyChange={setSelectedCompanyId}
                />
              </PanelWrapper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <PanelWrapper
                stackedLayout={stackedLayout}
                sx={{ p: 2, mb: 2 }}
                dividerSx={{ my: 2 }}
              >
                <PanelTitle
                  stackedLayout={stackedLayout}
                  sx={{ mb: stackedLayout ? 1 : 2, mt: stackedLayout ? 2 : 0 }}
                  title={t('account.form.account_configuration')}
                />

                <AccountConfigurationPanel
                  ref={accountConfigurationPanelRef}
                  account={account}
                  apiResponseErrors={apiResponseErrors}
                />
              </PanelWrapper>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: stackedLayout ? 12 : 6 }}>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <PanelWrapper
                stackedLayout={stackedLayout}
                sx={{ p: 2, mb: 2, overflow: 'visible' }}
                dividerSx={{ mb: 2 }}
              >
                <PanelTitle
                  stackedLayout={stackedLayout}
                  title={t('account.form.contact_information')}
                />

                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('account.form.primary_account_contact_hint')}
                </Typography>

                <ConnectedAccountContactPanel
                  ref={connectedAccountContactPanelRef}
                  account={account}
                  apiResponseErrors={apiResponseErrors}
                />
              </PanelWrapper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <PanelWrapper
                stackedLayout={stackedLayout}
                sx={{ p: 2, mb: 2 }}
                dividerSx={{ my: 2 }}
              >
                <PanelTitle
                  stackedLayout={stackedLayout}
                  title={t('form_fields.notes')}
                  sx={{ mt: stackedLayout ? 2 : 0 }}
                />

                <NotesPanel ref={notesPanelRef} account={account} />
              </PanelWrapper>
            </Grid>

            {isEditing && (
              <Grid size={{ xs: 12 }}>
                <PanelWrapper
                  stackedLayout={stackedLayout}
                  sx={{ p: 2, mb: 2 }}
                  dividerSx={{ my: 2 }}
                >
                  <PanelTitle
                    stackedLayout={stackedLayout}
                    title={t('form_fields.truck_capacity')}
                    sx={{ mt: stackedLayout ? 2 : 0, mb: stackedLayout ? 1 : 0 }}
                  />

                  <TruckCapacityPanel
                    ref={truckCapacityPanelRef}
                    account={account}
                    allowTypedRows
                    apiResponseErrors={apiResponseErrors}
                    companyId={companyId}
                    isConnectedAccount
                  />
                </PanelWrapper>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ mt: 3 }}>
            {account?.id ? (
              <CompliancePanel
                compliantType={CompliantType.ACCOUNT}
                compliantId={account.id}
                onEditingStateChange={onComplianceEditingChange}
              />
            ) : (
              <CompliancePanel
                compliantType={CompliantType.ACCOUNT}
                companyId={selectedCompanyId}
                stagedMode
                onStagedDocsChange={onStagedComplianceDocsChange}
              />
            )}
          </Box>

          <MatchKeysSection
            companyId={companyId}
            matchableId={account?.id}
            matchableType={MatchKeyMatchableType.ACCOUNT}
            recordName={account?.name ?? ''}
          />
        </Grid>
      </Grid>
    );
  },
);

interface PanelWrapperProps {
  children: React.ReactNode;
  dividerSx?: SxProps<Theme>;
  stackedLayout?: boolean;
  sx?: SxProps<Theme>;
}

function PanelWrapper({ children, dividerSx, stackedLayout, sx }: PanelWrapperProps) {
  if (stackedLayout) {
    return (
      <React.Fragment>
        {dividerSx && <Divider sx={dividerSx} />}
        {children}
      </React.Fragment>
    );
  }

  return <Card sx={sx}>{children}</Card>;
}

interface PanelTitleProps {
  stackedLayout?: boolean;
  sx?: SxProps<Theme>;
  title: string;
}

function PanelTitle({ stackedLayout, sx, title }: PanelTitleProps) {
  if (stackedLayout) {
    return (
      <Typography
        sx={[
          (theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '14px',
            fontWeight: 600,
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {title}
      </Typography>
    );
  }

  return (
    <Typography variant="form-section-title" sx={sx as SxProps}>
      {title}
    </Typography>
  );
}

export default VendorAccountForm;
